/* ============================================================================
 * Centralized auth state for the SPA.
 *
 * IMPORTANT — what lives in localStorage:
 *
 * tt:token — short-lived access JWT (10h, signed)
 * tt:refreshToken — opaque refresh token used against /api/auth/refresh
 *
 * That's it. NO user profile, NO id, NO role.
 *
 * Why? Anything in`localStorage`is readable by any XSS that lands in
 * the page. The less identity-shaped data we leave sitting there the
 * less an attacker can mine. The JWT already carries (signed) the
 * fields we need to render the UI —`id`,`role`,`full_name`,
 *`email`— so we decode the token on demand instead of mirroring a
 *`tt:user`JSON blob alongside it.
 *
 * The decoder DELIBERATELY does not verify the signature: the server is
 * the only party that needs to trust the token. The client just reads
 * the claims to render the right page / sidebar / greeting. Worst case
 * a malicious script edits the token in storage to change`role`→ the
 * server still rejects it on the next API call.
 * ============================================================================ */

export type Role = "ADMIN" | "PROJECT_MANAGER" | "EMPLOYEE";

// Landing page preference (must be after type declaration to avoid circular deps)
import { landingPageToPath } from "./settings";

export interface CurrentUser {
 id: number;
 name: string;
 email: string;
 role: Role;
}

export const TOKEN_KEY ="tt:token";
export const REFRESH_TOKEN_KEY ="tt:refreshToken";
const ROLE_CONTEXT_KEY ="tt:roleContext";


/* ----------------------------------------------------------------------------
 * Token decoding
 *
 * A JWT is`header.payload.signature`, base64url-encoded. We only need
 * the payload, and only to render UI bits — no signature verification.
 * -------------------------------------------------------------------------- */

interface DecodedTokenClaims {
 id?: number;
 role?: string;
 email?: string;
 full_name?: string;
 exp?: number;
}

const base64UrlDecode = (input: string): string => {
 let s = input.replace(/-/g,"+").replace(/_/g,"/");
 const pad = s.length % 4;
 if (pad === 2) s +="==";
 else if (pad === 3) s +="=";
 else if (pad !== 0) return""; // malformed
 try {
 if (typeof atob ==="function") return atob(s);
 } catch {
 return"";
 }
 return"";
};

const decodeToken = (token: string | null): DecodedTokenClaims | null => {
 if (!token) return null;
 const parts = token.split(".");
 if (parts.length !== 3) return null;
 const raw = base64UrlDecode(parts[1]!);
 if (!raw) return null;
 try {
 const parsed = JSON.parse(raw) as DecodedTokenClaims;
 if (typeof parsed !=="object"|| parsed === null) return null;
 return parsed;
 } catch {
 return null;
 }
};

/* ----------------------------------------------------------------------------
 * Readers
 * -------------------------------------------------------------------------- */

export function getToken(): string | null {
 if (typeof window ==="undefined") return null;
 return window.localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
 if (typeof window ==="undefined") return null;
 return window.localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getCurrentUser(): CurrentUser | null {
 const claims = decodeToken(getToken());
 if (!claims || !claims.id || !claims.role) return null;

 const role = String(claims.role).trim().toUpperCase();

 // Map any role to one of the recognized values.
 // Admin stays admin, Project Manager stays PM, everything else is Employee.
 let mappedRole: Role;
 if (role ==="ADMIN") mappedRole ="ADMIN";
 else if (role ==="PROJECT_MANAGER") mappedRole ="PROJECT_MANAGER";
 else mappedRole ="EMPLOYEE";

 // Reject tokens whose`exp`has already passed — the API interceptor
 // will refresh them on the next call, but until then the user should
 // be treated as anonymous.
 if (typeof claims.exp ==="number"&& claims.exp * 1000 <= Date.now()) {
 return null;
 }

 return {
 id: Number(claims.id),
 name: String(claims.full_name ??""),
 email: String(claims.email ??""),
 role: mappedRole,
 };
}

export function getRole(): Role | null {
 return getCurrentUser()?.role ?? null;
}

export function getUserId(): number | null {
 return getCurrentUser()?.id ?? null;
}

export function isAuthenticated(): boolean {
 return Boolean(getToken() && getCurrentUser());
}

/* ----------------------------------------------------------------------------
 * Mutators
 * -------------------------------------------------------------------------- */

export interface SessionPayload {
 token: string;
 refreshToken?: string | null;
}

/** Persist a login result. Only the tokens are stored — the user
 * profile is read back from the JWT itself via`getCurrentUser()`. */
export function setSession(payload: SessionPayload): void {
 if (typeof window ==="undefined") return;
 window.localStorage.setItem(TOKEN_KEY, payload.token);
 if (payload.refreshToken) {
 window.localStorage.setItem(REFRESH_TOKEN_KEY, payload.refreshToken);
 } else {
 window.localStorage.removeItem(REFRESH_TOKEN_KEY);
 }
 // Clear password-change status cache so it re-checks on fresh login
 try { window.sessionStorage.removeItem("tt:pw-change-status"); } catch {}
}

/** Update only the tokens (used by the refresh-on-401 interceptor). */
export function setTokens(token: string, refreshToken?: string | null): void {
 if (typeof window ==="undefined") return;
 window.localStorage.setItem(TOKEN_KEY, token);
 if (refreshToken) {
 window.localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
 }
}

export function clearSession(): void {
 if (typeof window ==="undefined") return;
 // Clear tokens
 window.localStorage.removeItem(TOKEN_KEY);
 window.localStorage.removeItem(REFRESH_TOKEN_KEY);
 // Defensively wipe the legacy keys that pre-token-only builds used
 // to write, so an upgraded client doesn't carry stale identity in
 // localStorage forever.
 window.localStorage.removeItem("tt:user");
 window.localStorage.removeItem("tt:managerId");
 window.localStorage.removeItem("tt:employeeId");
 window.localStorage.removeItem("user");
 window.sessionStorage.removeItem(ROLE_CONTEXT_KEY);

 // Clear ALL sessionStorage (tab states, permission caches)
 window.sessionStorage.clear();

 // Clear any localStorage items that are app-specific caches
 const keysToRemove: string[] = [];
 for (let i = 0; i < window.localStorage.length; i++) {
 const key = window.localStorage.key(i);
 if (key && (key.startsWith("tt: ") || key.startsWith("project-") || key.startsWith("migrations-") || key.startsWith("leaves-"))) {
 keysToRemove.push(key);
 }
 }
 for (const key of keysToRemove) {
 window.localStorage.removeItem(key);
 }
}

/**
 * Server-side logout + local cleanup. Hits POST /api/auth/logout to add
 * the access-token jti to the revocation blocklist (so a copy that
 * leaked elsewhere immediately stops working) then wipes localStorage.
 *
 * Defined here, not in API.tsx, to avoid a circular import — API.tsx
 * imports from this file already. We dynamic-`import()`the API client
 * at call time.
 */
export async function signOut(): Promise<void> {
 const refreshToken = getRefreshToken();
 try {
 const { default: API } = await import("./API");
 await API.post("/api/auth/logout", { refreshToken });
 } catch {
 // Best-effort: even if the server is unreachable we still want the
 // local session cleared so the user sees the login screen.
 } finally {
 clearSession();
 // Clear in-memory caches to prevent stale data for next user
 import("../hooks/usePortalPermissions").then((m) => m.clearPortalPermissionsCache()).catch(() => {});
 import("../hooks/useProjectPermissions").then((m) => m.clearProjectPermissionsCache()).catch(() => {});
 }
}

export function homePathForRole(role: Role | null | undefined, userId?: number | string): string {
 if (!role) return"/";
 return landingPageToPath(undefined, userId);
}

/* ---------- Forbidden pub/sub ----------
 * The axios response interceptor calls notifyForbidden(message) whenever the
 * server replies 403. SidebarLayout subscribes once and renders a transient
 * toast.
 */
type ForbiddenHandler = (message: string) => void;
const forbiddenHandlers = new Set<ForbiddenHandler>();

export function onForbidden(handler: ForbiddenHandler): () => void {
 forbiddenHandlers.add(handler);
 return () => {
 forbiddenHandlers.delete(handler);
 };
}

export function notifyForbidden(message: string): void {
 for (const h of forbiddenHandlers) {
 try {
 h(message);
 } catch {
 // ignore handler errors
 }
 }
}

/* ----------------------------------------------------------------------------
 * Password-change OTP flow
 *
 * Backend endpoints (Chunk 3 of the change-password plan):
 * POST /api/auth/change-password/request-otp
 * POST /api/auth/change-password/verify-otp { otp }
 * PATCH /api/auth/change-password/confirm { newPassword }
 * GET /api/auth/change-password/status
 *
 * These are thin wrappers — they only translate HTTP into typed JS
 * promises. The actual session/lock semantics live in the API client's
 * response interceptor (see ./API.tsx), which will surface a 403 with
 *`code:'PASSWORD_CHANGE_REQUIRED'`when the user is locked.
 * -------------------------------------------------------------------------- */

export type PasswordChangeOtpStatus =
 |"OK" | "OTP_EXPIRED" | "INVALID" | "LOCKED";

export interface PasswordChangeStatus {
 passwordChangeRequired: boolean;
 alreadyChanged?: boolean;
}

export interface VerifyOtpResponse {
 status: PasswordChangeOtpStatus;
}

interface ApiResponse<T> {
 success: boolean;
 data?: T;
 message?: string;
}

/**
 * Kick off the password-change flow: generates an OTP, emails it to the
 * current user, and flips the durable`password_change_required`flag.
 *
 * In the voluntary`/change-password`flow the user supplies their
 * current password so the server can verify identity before issuing an
 * OTP. Omitting it (or passing an empty string) preserves the legacy
 * mandatory-lock behaviour where the user was already forced into the
 * page by middleware.
 */
export async function requestPasswordChangeOtp(
 currentPassword?: string,
): Promise<void> {
 const { default: API } = await import("./API");
 const body =
 typeof currentPassword ==="string"&& currentPassword.length > 0
 ? { currentPassword }
 : undefined;
 const res = await API.post<ApiResponse<undefined>>("/api/auth/change-password/request-otp",
 body
 );
 if (!res.data.success) {
 throw new Error(res.data.message ||"Failed to request password-change OTP");
 }
}

/**
 * Validate the OTP the user received by email. Returns a status enum —
 *`OK`means the user is verified and may proceed to`confirmPasswordChange`;
 *`OTP_EXPIRED`/`INVALID`/`LOCKED`mean the user must request a new
 * OTP (or is locked out for repeated failures).
 */
export async function verifyPasswordChangeOtp(
 otp: string
): Promise<VerifyOtpResponse> {
 const { default: API } = await import("./API");
 const res = await API.post<ApiResponse<VerifyOtpResponse>>("/api/auth/change-password/verify-otp",
 { otp }
 );
 if (!res.data.success || !res.data.data) {
 throw new Error(res.data.message ||"Failed to verify OTP");
 }
 return res.data.data;
}

/**
 * Finalise the password change. Must be called after a successful
 *`verifyPasswordChangeOtp`— the backend rejects with 403 if the
 * verification key has expired.
 */
export async function confirmPasswordChange(newPassword: string): Promise<void> {
 const { default: API } = await import("./API");
 const res = await API.patch<ApiResponse<undefined>>("/api/auth/change-password/confirm",
 { newPassword }
 );
 if (!res.data.success) {
 throw new Error(res.data.message ||"Failed to change password");
 }
}

/**
 * Returns whether the current user is currently locked out of the app
 * until they complete the password-change flow. Used by the frontend
 * route guard on mount.
 */
export async function getPasswordChangeStatus(): Promise<PasswordChangeStatus> {
 const { default: API } = await import("./API");
 const res = await API.get<ApiResponse<PasswordChangeStatus>>("/api/auth/change-password/status");
 if (!res.data.success || !res.data.data) {
 throw new Error(res.data.message ||"Failed to fetch password-change status");
 }
 return res.data.data;
}
